import { X, Mail, Check } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useState } from "react";

interface AuthModalProps {
  onClose: () => void;
  onGoTerms?: () => void;
  onGoPrivacy?: () => void;
}

export default function AuthModal({ onClose, onGoTerms, onGoPrivacy }: AuthModalProps) {
  const [agreed, setAgreed] = useState(false);

  const handleGoogleLogin = async () => {
    if (!agreed) return;
    // Capture referral code from URL before OAuth redirect
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    if (refCode) {
      localStorage.setItem('lynx_referral_code', refCode);
    }
    await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
      },
    });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md rounded-3xl border border-white/10 bg-zinc-900 p-8 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute right-5 top-5 text-zinc-600 hover:text-zinc-300 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Icon */}
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/20 mb-6">
          <Mail className="h-7 w-7 text-white" />
        </div>

        <h2 className="text-2xl font-semibold text-white mb-2">
          Sign in to Lynx Reach
        </h2>
        <p className="text-sm text-zinc-500 mb-8 leading-relaxed">
          Connect your Google account to automate your personalized job search
          and land your dream job faster.
        </p>

        {/* Terms Checkbox */}
        <label className="flex items-start gap-3 mb-6 cursor-pointer group">
          <div className="relative flex items-center justify-center mt-0.5">
            <input 
              type="checkbox" 
              className="peer sr-only" 
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <div className="h-5 w-5 rounded border border-white/20 bg-white/5 transition-all peer-checked:border-rose-500 peer-checked:bg-rose-500 group-hover:border-white/40"></div>
            <Check className="absolute h-3.5 w-3.5 text-white opacity-0 transition-opacity peer-checked:opacity-100 pointer-events-none" strokeWidth={3} />
          </div>
          <span className="text-sm text-zinc-400 leading-relaxed">
            I have read and agree to the company{" "}
            <button 
              onClick={(e) => { e.preventDefault(); onClose(); onGoTerms?.(); }} 
              className="text-zinc-300 hover:text-white underline decoration-white/20 underline-offset-2 transition-colors"
            >
              Terms of Service
            </button>{" "}
            and{" "}
            <button 
              onClick={(e) => { e.preventDefault(); onClose(); onGoPrivacy?.(); }} 
              className="text-zinc-300 hover:text-white underline decoration-white/20 underline-offset-2 transition-colors"
            >
              Privacy Policy
            </button>.
          </span>
        </label>

        {/* Google OAuth button */}
        <button
          onClick={handleGoogleLogin}
          disabled={!agreed}
          className={`flex w-full items-center justify-center gap-3 rounded-2xl border px-6 py-4 text-sm font-semibold transition-all ${
            agreed 
              ? "border-white/10 bg-white/5 text-white hover:bg-white/10 hover:border-white/20 active:scale-[0.98]" 
              : "border-white/5 bg-white/5 text-zinc-600 cursor-not-allowed"
          }`}
        >
          {/* Google icon */}
          <svg className="h-5 w-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
          Continue with Google
        </button>

        <p className="mt-4 text-center text-xs text-zinc-500 leading-relaxed px-2">
          Google Sign-In securely connects your Gmail account so Lynx Reach can send job outreach emails with your permission. We do not read your inbox.
        </p>
      </div>
    </div>
  );
}
